import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Modal, Button, Spinner, Table } from 'reactstrap';
import usePermission from '../Hooks/usePermission';
import {
  fetchTimeEntries,
  startTimeEntry,
  stopTimeEntry,
  addManualTimeEntry,
  deleteTimeEntry,
} from '../store/timeEntry/actions';
import { formatCurrency } from '../helpers/formatCurrency';

/**
 * Per-technician labor time logging on a single work order.
 *
 * Visibility rules:
 *  - cost:read-wo-total → user sees rate + cost columns
 *  - time-entry:log-others → user sees all techs' entries; otherwise only own
 *  - time-entry:log-own → user can use Start/Stop + Add manual for self
 *
 * Status gates are enforced server-side; the UI mirrors them to hide buttons when blocked.
 */
const STATUS_BLOCKS = {
  SUBMITTED: 'submitted_blocks_entry',
  RESOLVED: 'resolved_blocks_entry',
};

const formatDuration = (seconds) => {
  if (seconds == null) return '—';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h === 0) return `${m}m`;
  return `${h}h ${m}m`;
};

// Parse the LocalDateTime string the backend emits ("yyyy-MM-dd'T'HH:mm:ss").
// The backend stores all timestamps in UTC; the trailing 'Z' tells JS that.
// Without it, browsers interpret naive ISO strings as *local* time, which would
// drift the display by the user's offset.
const parseServerDateTime = (value) => {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value === 'string') {
    const hasTz = /[Zz]|[+-]\d{2}:?\d{2}$/.test(value);
    const d = new Date(hasTz ? value : `${value}Z`);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  // Some Jackson setups emit timestamps as [y,M,d,h,m,s,nanos]
  if (Array.isArray(value) && value.length >= 3) {
    const [y, mo, d, h = 0, mi = 0, s = 0] = value;
    const dt = new Date(Date.UTC(y, (mo || 1) - 1, d || 1, h, mi, s));
    return Number.isNaN(dt.getTime()) ? null : dt;
  }
  return null;
};

const formatDateTime = (value) => {
  const d = parseServerDateTime(value);
  if (!d) {
    if (value != null && typeof console !== 'undefined') {
      // eslint-disable-next-line no-console
      console.warn('TimeEntrySection: unparseable date value', value);
    }
    return '—';
  }
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${day}/${month}/${year} ${hh}:${mm}`;
};

const elapsedSeconds = (startValue, nowMs) => {
  const start = parseServerDateTime(startValue);
  if (!start) return null;
  const secs = Math.max(0, Math.floor((nowMs - start.getTime()) / 1000));
  return secs;
};

const TimeEntrySection = ({ workOrderId, workOrderStatus, currentUserId }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { hasPermission } = usePermission();

  const entries = useSelector((s) => s.TimeEntry?.entries || []);
  const entriesLoading = useSelector((s) => s.TimeEntry?.entriesLoading);
  const entriesError = useSelector((s) => s.TimeEntry?.entriesError);
  const mutating = useSelector((s) => s.TimeEntry?.mutating);
  const woCurrency = useSelector((s) => s.WorkOrderCost?.cost?.currency);
  // Fallback when no WO cost record exists yet (new WO) — read org currency from the active context.
  const activeOrgCurrency = useSelector((s) => s.ActiveContext?.activeOrganizationCurrency);

  const canSeeCost = hasPermission('cost:read-wo-total');
  const canSeeAllEntries = hasPermission('time-entry:log-others');
  const canLogOwn = hasPermission('time-entry:log-own');
  const canEditOthers = hasPermission('time-entry:log-others');
  // Mirror server-side permission gate on GET /work-order-time-entry: callers
  // without either log permission would 403; render nothing for them so the
  // section can be safely stacked into a tab that's visible to all roles.
  const canAccessTimeEntries = canLogOwn || canSeeAllEntries;

  const [manualOpen, setManualOpen] = useState(false);
  const [manualForm, setManualForm] = useState({ startedAt: '', endedAt: '', notes: '', hourlyRateOverride: '' });
  const [manualErr, setManualErr] = useState(null);
  const [nowMs, setNowMs] = useState(() => Date.now());

  useEffect(() => {
    if (workOrderId && canAccessTimeEntries) dispatch(fetchTimeEntries(workOrderId));
  }, [dispatch, workOrderId, canAccessTimeEntries]);

  const visibleEntries = useMemo(() => {
    if (canSeeAllEntries) return entries;
    return entries.filter((e) => e.technicianId === currentUserId);
  }, [entries, canSeeAllEntries, currentUserId]);

  const runningEntryForMe = useMemo(
    () => visibleEntries.find((e) => e.running && e.technicianId === currentUserId) || null,
    [visibleEntries, currentUserId]
  );

  // Tick once a minute while any entry is running so the live duration in the
  // Duration column stays current without thrashing renders.
  const hasRunning = useMemo(() => visibleEntries.some((e) => e.running), [visibleEntries]);
  useEffect(() => {
    if (!hasRunning) return undefined;
    const id = setInterval(() => setNowMs(Date.now()), 60_000);
    return () => clearInterval(id);
  }, [hasRunning]);

  // Gate placed after all hooks so hook order stays stable across renders
  // (React rules-of-hooks). Renders nothing for callers without either log
  // permission — they'd otherwise 403 on the GET below.
  if (!canAccessTimeEntries) return null;

  const statusBlocksNew = STATUS_BLOCKS[workOrderStatus] || null;
  const canStart = !statusBlocksNew && canLogOwn && !runningEntryForMe;
  const canStop = !!runningEntryForMe;
  const canAddManual = !statusBlocksNew && canLogOwn;

  const handleStart = () => {
    dispatch(startTimeEntry(workOrderId, null, (err) => {
      if (!err) dispatch(fetchTimeEntries(workOrderId));
    }));
  };

  const handleStop = () => {
    dispatch(stopTimeEntry(workOrderId, (err) => {
      if (!err) dispatch(fetchTimeEntries(workOrderId));
    }));
  };

  const handleManualSave = () => {
    setManualErr(null);
    if (!manualForm.startedAt || !manualForm.endedAt) {
      setManualErr(t('cost_tracking.time_entry.manual_modal.error_required', 'Start and end are required.'));
      return;
    }
    if (new Date(manualForm.endedAt) <= new Date(manualForm.startedAt)) {
      setManualErr(t('cost_tracking.time_entry.manual_modal.error_invalid_range', 'End must be after start.'));
      return;
    }
    const rawOverride = manualForm.hourlyRateOverride;
    let hourlyRateOverride = null;
    if (rawOverride !== '' && rawOverride !== null && rawOverride !== undefined) {
      const parsed = Number(rawOverride);
      if (Number.isNaN(parsed) || parsed < 0) {
        setManualErr(t('cost_tracking.time_entry.manual_modal.error_invalid_rate', 'Hourly rate must be 0 or higher.'));
        return;
      }
      hourlyRateOverride = parsed;
    }

    dispatch(addManualTimeEntry({
      workOrderId,
      startedAt: manualForm.startedAt,
      endedAt: manualForm.endedAt,
      notes: manualForm.notes || null,
      hourlyRateOverride,
    }, (err) => {
      if (!err) {
        setManualOpen(false);
        setManualForm({ startedAt: '', endedAt: '', notes: '', hourlyRateOverride: '' });
        dispatch(fetchTimeEntries(workOrderId));
      } else {
        setManualErr(err);
      }
    }));
  };

  const handleDelete = (id) => {
    dispatch(deleteTimeEntry(id, (err) => {
      if (!err) dispatch(fetchTimeEntries(workOrderId));
    }));
  };

  return (
    <div className="time-entry-section">
      <div className="d-flex justify-content-between align-items-start mb-3">
        <div>
          <h5 className="mb-1">{t('cost_tracking.time_entry.section_title', 'Time logged')}</h5>
          <small className="text-muted">
            {t('cost_tracking.time_entry.section_subtitle', 'Per-technician labor time on this work order')}
          </small>
        </div>
        <div className="d-flex" style={{ gap: '8px' }}>
          {canStart && (
            <Button color="success" disabled={mutating} onClick={handleStart}>
              {t('cost_tracking.time_entry.start_timer', 'Start timer')}
            </Button>
          )}
          {canStop && (
            <Button color="warning" disabled={mutating} onClick={handleStop}>
              {t('cost_tracking.time_entry.stop_timer', 'Stop timer')}
            </Button>
          )}
          {canAddManual && (
            <Button color="primary" outline disabled={mutating} onClick={() => setManualOpen(true)}>
              {t('cost_tracking.time_entry.add_manual', 'Add manual entry')}
            </Button>
          )}
        </div>
      </div>

      {statusBlocksNew && (
        <div className="alert alert-info">
          {t(`cost_tracking.time_entry.${statusBlocksNew}`, 'Time logging is not available for this work order status.')}
        </div>
      )}

      {entriesLoading && (
        <div className="text-center py-3">
          <Spinner size="sm" /> {t('cost_tracking.time_entry.loading', 'Loading time entries…')}
        </div>
      )}

      {entriesError && (
        <div className="alert alert-danger">{entriesError}</div>
      )}

      {!entriesLoading && visibleEntries.length === 0 && (
        <div className="text-muted text-center py-3">
          {t('cost_tracking.time_entry.no_entries', 'No time entries yet.')}
        </div>
      )}

      {visibleEntries.length > 0 && (
        <Table responsive size="sm" className="align-middle">
          <thead>
            <tr>
              <th>{t('cost_tracking.time_entry.table.technician', 'Technician')}</th>
              <th>{t('cost_tracking.time_entry.table.started_at', 'Started')}</th>
              <th>{t('cost_tracking.time_entry.table.ended_at', 'Ended')}</th>
              <th>{t('cost_tracking.time_entry.table.duration', 'Duration')}</th>
              <th>{t('cost_tracking.time_entry.table.source', 'Source')}</th>
              {canSeeCost && <th>{t('cost_tracking.time_entry.table.rate', 'Rate')}</th>}
              {canSeeCost && <th>{t('cost_tracking.time_entry.table.cost', 'Cost')}</th>}
              {canEditOthers && <th>{t('cost_tracking.time_entry.table.actions', 'Actions')}</th>}
            </tr>
          </thead>
          <tbody>
            {visibleEntries.map((e) => (
              <tr key={e.id}>
                <td>{e.technicianName || `#${e.technicianId ?? '—'}`}</td>
                <td>{formatDateTime(e.startedAt)}</td>
                <td>
                  {e.running
                    ? <span className="badge bg-success">{t('cost_tracking.time_entry.running_label', 'Running')}</span>
                    : formatDateTime(e.endedAt)}
                </td>
                <td>{formatDuration(e.running ? elapsedSeconds(e.startedAt, nowMs) : e.durationSeconds)}</td>
                <td>{t(`cost_tracking.time_entry.source.${e.source}`, e.source)}</td>
                {canSeeCost && <td>{formatCurrency(e.hourlyRateSnapshot, woCurrency)}</td>}
                {canSeeCost && <td>{formatCurrency(e.costSnapshot, woCurrency)}</td>}
                {canEditOthers && (
                  <td>
                    <Button size="sm" color="link" className="text-danger p-0" onClick={() => handleDelete(e.id)}>
                      {t('shared.delete', 'Delete')}
                    </Button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      <Modal isOpen={manualOpen} toggle={() => setManualOpen(false)} centered>
        <div className="modal-header">
          <h5 className="modal-title">{t('cost_tracking.time_entry.manual_modal.title', 'Add manual time entry')}</h5>
          <button type="button" className="btn-close" onClick={() => setManualOpen(false)} />
        </div>
        <div className="modal-body">
          {manualErr && <div className="alert alert-danger">{manualErr}</div>}
          <div className="mb-3">
            <label className="form-label">
              {t('cost_tracking.time_entry.manual_modal.started_at', 'Start')}
            </label>
            <input
              type="datetime-local"
              className="form-control"
              value={manualForm.startedAt}
              onChange={(e) => setManualForm({ ...manualForm, startedAt: e.target.value })}
            />
          </div>
          <div className="mb-3">
            <label className="form-label">
              {t('cost_tracking.time_entry.manual_modal.ended_at', 'End')}
            </label>
            <input
              type="datetime-local"
              className="form-control"
              value={manualForm.endedAt}
              onChange={(e) => setManualForm({ ...manualForm, endedAt: e.target.value })}
            />
          </div>
          <div className="mb-3">
            <label className="form-label">
              {t('cost_tracking.time_entry.manual_modal.hourly_rate_override', 'Hourly rate (optional)')}
            </label>
            <div className="input-group">
              <input
                type="number"
                step="0.01"
                min="0"
                className="form-control"
                placeholder={t('cost_tracking.time_entry.manual_modal.hourly_rate_placeholder', 'Defaults to your hourly rate; 0 = no labor cost')}
                value={manualForm.hourlyRateOverride}
                onChange={(e) => setManualForm({ ...manualForm, hourlyRateOverride: e.target.value })}
              />
              <span className="input-group-text">{woCurrency || activeOrgCurrency || 'USD'}</span>
            </div>
            <small className="text-muted">
              {t('cost_tracking.time_entry.manual_modal.hourly_rate_hint', 'Leave blank to use your default rate. Enter 0 for no labor cost.')}
            </small>
          </div>
          <div className="mb-3">
            <label className="form-label">
              {t('cost_tracking.time_entry.manual_modal.notes', 'Notes (optional)')}
            </label>
            <textarea
              className="form-control"
              rows={2}
              value={manualForm.notes}
              onChange={(e) => setManualForm({ ...manualForm, notes: e.target.value })}
            />
          </div>
        </div>
        <div className="modal-footer">
          <Button color="secondary" outline onClick={() => setManualOpen(false)} disabled={mutating}>
            {t('cost_tracking.time_entry.manual_modal.cancel', 'Cancel')}
          </Button>
          <Button color="primary" onClick={handleManualSave} disabled={mutating}>
            {t('cost_tracking.time_entry.manual_modal.save', 'Save')}
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default TimeEntrySection;
